import React from "react";
import { Server, LineChart, KeyRound, Volume2, Trash2 } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[var(--accent)] transition-colors";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[var(--t2)]">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

const VOICE_ENGINES = [
  ["elevenlabs", "ElevenLabs"],
  ["azure", "Azure Neural"],
  ["streamelements", "StreamElements"],
  ["voicerss", "VoiceRSS"],
  ["browser", "Browser TTS"],
];

export default function SettingsTab() {
  const { settings: s, updateSettings, resetSettings } = useApp();

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <Overline>Configuration</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Settings</h2>
      </div>

      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><Server size={16} className="text-[var(--accent)]" /><Overline>MQTT Broker</Overline></div>
        <Field label="Host"><input data-testid="set-mqtt-host" className={inputCls} value={s.mqttHost} onChange={(e) => updateSettings({ mqttHost: e.target.value })} /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Username"><input data-testid="set-mqtt-user" className={inputCls} value={s.mqttUser} onChange={(e) => updateSettings({ mqttUser: e.target.value })} /></Field>
          <Field label="Password"><input data-testid="set-mqtt-pass" type="password" className={inputCls} value={s.mqttPass} onChange={(e) => updateSettings({ mqttPass: e.target.value })} /></Field>
        </div>
        <button data-testid="set-mqtt-save" onClick={() => toast.success("MQTT settings saved — reconnecting…")} className="px-4 py-2 rounded-md bg-[var(--accent)] text-black text-sm font-medium active:scale-95 transition-transform">Save & Reconnect</button>
      </Panel>

      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><LineChart size={16} className="text-[var(--accent)]" /><Overline>Grafana</Overline></div>
        <Field label="Dashboard URL"><input data-testid="set-grafana-url" className={inputCls} value={s.grafanaUrl} onChange={(e) => updateSettings({ grafanaUrl: e.target.value })} /></Field>
      </Panel>

      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><KeyRound size={16} className="text-[var(--accent)]" /><Overline>AI Keys</Overline></div>
        <Field label="Gemini API Keys (one per line)"><textarea data-testid="set-gemini-keys" rows={3} className={inputCls} value={s.geminiKeys} onChange={(e) => updateSettings({ geminiKeys: e.target.value })} placeholder="AIzaSy..." /></Field>
        <Field label="DeepSeek Fallback Key"><input data-testid="set-deepseek-key" className={inputCls} value={s.deepseekKey} onChange={(e) => updateSettings({ deepseekKey: e.target.value })} placeholder="sk-..." /></Field>
      </Panel>

      <Panel className="p-6 space-y-4">
        <div className="flex items-center gap-2"><Volume2 size={16} className="text-[var(--accent)]" /><Overline>AI Voice Engine</Overline></div>
        <Field label="Provider">
          <select data-testid="set-voice-engine" className={inputCls} value={s.voiceEngine} onChange={(e) => updateSettings({ voiceEngine: e.target.value })}>
            {VOICE_ENGINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Field>

        {s.voiceEngine === "elevenlabs" && (
          <Field label="ElevenLabs API Key"><input data-testid="set-eleven-key" className={inputCls} value={s.elevenKey} onChange={(e) => updateSettings({ elevenKey: e.target.value })} /></Field>
        )}
        {s.voiceEngine === "azure" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Azure Key"><input data-testid="set-azure-key" className={inputCls} value={s.azureKey} onChange={(e) => updateSettings({ azureKey: e.target.value })} /></Field>
            <Field label="Region"><input data-testid="set-azure-region" className={inputCls} value={s.azureRegion} onChange={(e) => updateSettings({ azureRegion: e.target.value })} /></Field>
          </div>
        )}
        {s.voiceEngine === "voicerss" && (
          <Field label="VoiceRSS Key"><input data-testid="set-voicerss-key" className={inputCls} value={s.voicerssKey} onChange={(e) => updateSettings({ voicerssKey: e.target.value })} /></Field>
        )}
        {s.voiceEngine === "streamelements" && <p className="text-xs text-[var(--t2)] font-mono">StreamElements requires no API key — free public TTS endpoint.</p>}
        {s.voiceEngine === "browser" && <p className="text-xs text-[var(--t2)] font-mono">Uses the built-in Web Speech Synthesis API. No key required.</p>}

        <Field label={`Speech Rate — ${s.speechRate.toFixed(1)}×`}>
          <input data-testid="set-speech-rate" type="range" min="0.5" max="2" step="0.1" value={s.speechRate} onChange={(e) => updateSettings({ speechRate: parseFloat(e.target.value) })} className="w-full accent-[var(--accent)]" />
        </Field>
      </Panel>

      <Panel className="p-6 border-[var(--danger)]/40">
        <Overline className="text-[var(--danger)]">Danger Zone</Overline>
        <p className="text-sm text-[var(--t2)] mt-2 mb-3">Permanently reset all configuration to factory defaults.</p>
        <button data-testid="set-reset-all" onClick={resetSettings} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--danger)]/50 text-[var(--danger)] text-sm font-medium hover:bg-[var(--danger)]/10 transition-colors">
          <Trash2 size={15} /> Reset All Configuration
        </button>
      </Panel>
    </div>
  );
}
